/**
 * Shared base class for all configurator custom elements.
 *
 * Owns the cascading dropdown logic (make → model → year → bed → inbed),
 * localStorage save/restore, GA4 + Sentry reporting, and cross-tab event
 * handling. Subclasses override hook methods for component-specific behavior.
 *
 * Hook methods (override in subclass):
 *   cacheAdditionalRefs()              cache subclass-specific DOM refs
 *   bindAdditionalEvents()             bind subclass-specific event listeners
 *   onSelectionComplete(selection, urls)  called when truck is fully selected
 *   onReset()                          called when truck is cleared externally
 *   onRestored(saved, urls)            called after localStorage restore
 *   getSource()                        returns a string for GA4 events
 *
 * Protected utilities (callable from subclass):
 *   this._saveTruck(selection, urls)   write truck + URLs to localStorage
 *   this._clearTruck()                 remove truck from localStorage + dispatch update event
 *   this._repopulateMakes()            reset form to initial state
 *   this._getUrlMap()                  returns promise for the URL map
 */

if (!window.ConfiguratorBase) {

window._configuratorUrlMapFailed = window._configuratorUrlMapFailed ?? false;

function _isConfiguratorUrlMapFailed() {
  return !!window._configuratorUrlMapFailed;
}

function _getConfiguratorUrlMap() {
  if (!window._configuratorUrlMapFetch) {
    const srcEl = document.getElementById('configurator-urls-src');
    const url = srcEl?.textContent?.trim();
    if (!url) {
      console.warn('[configurator] configurator-urls-src element missing or empty');
      window._configuratorUrlMapFetch = Promise.resolve({});
    } else {
      window._configuratorUrlMapFailed = false;
      window._configuratorUrlMapFetch = fetch(url)
        .then((r) => r.json())
        .catch((err) => {
          console.warn('[configurator] URL map fetch failed — links will not be rewritten:', url, err);
          window._configuratorUrlMapFailed = true;
          window._configuratorUrlMapFetch = null; // allow retry on next call
          return {};
        });
    }
  }
  return window._configuratorUrlMapFetch;
}

class ConfiguratorBase extends HTMLElement {
  #data = null;
  #selectedInbedEntry = null;

  connectedCallback() {
    this.#cacheBaseRefs();
    this.cacheAdditionalRefs();
    this.#loadData();
    if (!this.#data) return;
    _getConfiguratorUrlMap();
    this.#populateMakes();
    this.#bindBaseEvents();
    this.bindAdditionalEvents();
    // URL params take priority over localStorage; skip restore if params are present
    if (new URLSearchParams(window.location.search).get('make')) {
      this.#prepopulateFromUrl();
    } else {
      this.#restoreFromLocalStorage();
    }
    document.addEventListener('configurator:update', this.#handleExternalUpdate);
    window.addEventListener('storage', this.#handleStorageChange);
  }

  disconnectedCallback() {
    document.removeEventListener('configurator:update', this.#handleExternalUpdate);
    window.removeEventListener('storage', this.#handleStorageChange);
  }

  // -- Hooks (no-op defaults, override in subclass) --

  cacheAdditionalRefs() {}
  bindAdditionalEvents() {}
  async onSelectionComplete(_selection, _urls) {}
  onReset() {}
  async onRestored(_saved, _urls) {}
  getSource() { return 'unknown'; }

  // -- Protected utilities --

  _saveTruck(selection, urls) {
    localStorage.setItem('configuratorTruckInfo', JSON.stringify(selection));
    localStorage.setItem('configuratorUrlMap', JSON.stringify(urls));
  }

  _clearTruck() {
    localStorage.removeItem('configuratorTruckInfo');
    localStorage.removeItem('configuratorUrlMap');
    document.dispatchEvent(new CustomEvent('configurator:update', { detail: null }));
  }

  _repopulateMakes() {
    this.#populateMakes();
    this.#hideFieldsFrom('modelField');
    this.#hideFetchError();
  }

  _getUrlMap() {
    return _getConfiguratorUrlMap();
  }

  // -- Private: setup --

  #cacheBaseRefs() {
    this._makeSelect = this.querySelector('[data-ref="makeSelect"]');
    this._modelField = this.querySelector('[data-ref="modelField"]');
    this._modelSelect = this.querySelector('[data-ref="modelSelect"]');
    this._yearField = this.querySelector('[data-ref="yearField"]');
    this._yearSelect = this.querySelector('[data-ref="yearSelect"]');
    this._bedField = this.querySelector('[data-ref="bedField"]');
    this._bedSelect = this.querySelector('[data-ref="bedSelect"]');
    this._inbedField = this.querySelector('[data-ref="inbedField"]');
    this._inbedSelect = this.querySelector('[data-ref="inbedSelect"]');
    this._bedMirror = this.querySelector('[data-ref="bedMirror"]');
    this._inbedMirror = this.querySelector('[data-ref="inbedMirror"]');
    this._fetchError = this.querySelector('[data-ref="fetchError"]');

    // Capture placeholder texts from initial HTML so each component keeps its own wording.
    this._makePlaceholder = this._makeSelect?.options[0]?.text || 'Make';
    this._modelPlaceholder = this._modelSelect?.options[0]?.text || 'Model';
    this._yearPlaceholder = this._yearSelect?.options[0]?.text || 'Year';
    this._bedPlaceholder = this._bedSelect?.options[0]?.text || 'Bed Size';
    this._inbedPlaceholder = this._inbedSelect?.options[0]?.text || 'Select';
  }

  #loadData() {
    if (this.#data) return;
    const el = document.getElementById('configurator-data');
    if (!el) {
      this.#reportError('configurator_data_load_failed', { source: this.getSource(), reason: 'element_missing' });
      return;
    }
    try {
      this.#data = JSON.parse(el.textContent);
    } catch {
      this.#reportError('configurator_data_load_failed', { source: this.getSource(), reason: 'parse_error' });
    }
  }

  // -- Private: populate --

  #populateMakes() {
    this.#resetSelect(this._makeSelect, this._makePlaceholder);
    for (const entry of this.#data.options) {
      this._makeSelect.add(new Option(entry.make, entry.make));
    }
  }

  #populateModels(make) {
    const makeEntry = this.#data.options.find((e) => e.make === make);
    if (!makeEntry) return;
    this.#resetSelect(this._modelSelect, this._modelPlaceholder);
    for (const entry of makeEntry.models) {
      this._modelSelect.add(new Option(entry.model, entry.model));
    }
    this.#showField('modelField', 'modelSelect');
  }

  #populateYears(make, model) {
    const makeEntry = this.#data.options.find((e) => e.make === make);
    const modelEntry = makeEntry?.models.find((e) => e.model === model);
    if (!modelEntry) return;
    this.#resetSelect(this._yearSelect, this._yearPlaceholder);
    const sorted = [...modelEntry.years].sort((a, b) => parseInt(b.year) - parseInt(a.year));
    const seen = new Set();
    for (const entry of sorted) {
      if (seen.has(entry.year)) continue;
      seen.add(entry.year);
      this._yearSelect.add(new Option(entry.year, entry.year));
    }
    this.#showField('yearField', 'yearSelect');
  }

  #populateBeds(make, model, year) {
    const makeEntry = this.#data.options.find((e) => e.make === make);
    const modelEntry = makeEntry?.models.find((e) => e.model === model);
    const yearEntry = modelEntry?.years.find((e) => e.year === year);
    if (!yearEntry) return;
    this.#resetSelect(this._bedSelect, this._bedPlaceholder);
    const seen = new Set();
    for (const entry of yearEntry.beds) {
      if (seen.has(entry.bed)) continue;
      seen.add(entry.bed);
      this._bedSelect.add(new Option(entry.bed, entry.bed));
    }
    this.#showField('bedField', 'bedSelect');
  }

  // -- Private: cascade event bindings --

  #bindBaseEvents() {
    this._makeSelect?.addEventListener('change', () => this.#handleMakeChange());
    this._modelSelect?.addEventListener('change', () => this.#handleModelChange());
    this._yearSelect?.addEventListener('change', () => this.#handleYearChange());
    this._bedSelect?.addEventListener('change', () => this.#handleBedChange());
    this._inbedSelect?.addEventListener('change', () => this.#handleInbedChange());
  }

  // -- Private: cascade handlers --

  #handleMakeChange() {
    const make = this._makeSelect.value;
    this.#hideFieldsFrom('modelField');
    if (!make) return;
    this.#populateModels(make);
  }

  #handleModelChange() {
    const make = this._makeSelect.value;
    const model = this._modelSelect.value;
    this.#hideFieldsFrom('yearField');
    if (!model) return;
    this.#populateYears(make, model);
  }

  #handleYearChange() {
    const make = this._makeSelect.value;
    const model = this._modelSelect.value;
    const year = this._yearSelect.value;
    this.#hideFieldsFrom('bedField');
    if (!year) return;
    this.#populateBeds(make, model, year);
  }

  async #handleBedChange() {
    const make = this._makeSelect.value;
    const model = this._modelSelect.value;
    const year = this._yearSelect.value;
    const bed = this._bedSelect.value;
    this.#hideFieldsFrom('inbedField');
    if (this._bedMirror) this._bedMirror.textContent = bed || this._bedPlaceholder;
    if (!bed) return;

    const bedEntry = this.#findBedEntry(make, model, year, bed);
    if (!bedEntry) {
      this.#reportError('configurator_entry_not_found', { source: this.getSource(), make, model, year, bed });
      return;
    }

    const hasInbedOptions = bedEntry.inbeds.some((e) => e.inbed === 'Yes' || e.inbed === 'No');
    if (hasInbedOptions) {
      this.#resetSelect(this._inbedSelect, this._inbedPlaceholder);
      this._inbedSelect.add(new Option('No', 'No'));
      this._inbedSelect.add(new Option('Yes', 'Yes'));
      if (this._inbedMirror) this._inbedMirror.textContent = this._inbedPlaceholder;
      this.#showField('inbedField', 'inbedSelect');
    } else {
      this.#selectedInbedEntry = bedEntry.inbeds[0];
      await this.#handleSelectionComplete();
    }
  }

  async #handleInbedChange() {
    const inbedValue = this._inbedSelect.value;
    if (this._inbedMirror) this._inbedMirror.textContent = inbedValue || this._inbedPlaceholder;
    if (!inbedValue) return;

    const make = this._makeSelect.value;
    const model = this._modelSelect.value;
    const year = this._yearSelect.value;
    const bed = this._bedSelect.value;

    const bedEntry = this.#findBedEntry(make, model, year, bed);
    if (!bedEntry) {
      this.#reportError('configurator_entry_not_found', { source: this.getSource(), make, model, year, bed });
      return;
    }

    this.#selectedInbedEntry = bedEntry.inbeds.find(
      (e) => e.inbed === inbedValue || (inbedValue === 'No' && e.inbed === '')
    );
    if (!this.#selectedInbedEntry) {
      this.#reportError('configurator_inbed_entry_not_found', { source: this.getSource(), make, model, year, bed, inbed: inbedValue });
      return;
    }

    await this.#handleSelectionComplete();
  }

  async #handleSelectionComplete() {
    if (!this.#selectedInbedEntry) return;
    const selection = {
      make: this._makeSelect.value,
      model: this._modelSelect.value,
      year: this._yearSelect.value,
      bed: this._bedSelect.value,
      inbed: this._inbedSelect.value || '',
    };
    const key = `${selection.make}|${selection.model}|${selection.year}|${selection.bed}|${selection.inbed}`;
    let urlMap = await _getConfiguratorUrlMap();
    if (!Object.keys(urlMap).length) urlMap = await _getConfiguratorUrlMap();
    if (_isConfiguratorUrlMapFailed()) {
      this.#showFetchError();
      this.#reportError('configurator_url_map_fetch_failed', { source: this.getSource() });
    } else {
      this.#hideFetchError();
    }
    const urls = urlMap[key] || {};
    if (!Object.keys(urls).length) {
      console.warn('[configurator] key miss', { key, mapSize: Object.keys(urlMap).length, sample: Object.keys(urlMap)[0] });
    }
    this.#pushGA4Event('configurator_completed', { source: this.getSource(), ...selection });
    await this.onSelectionComplete(selection, urls);
  }

  // -- Private: URL-param prepopulation --

  async #prepopulateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const rawMake = params.get('make');
    if (!rawMake) return;

    const norm = (s) => (s || '').replace(/[–—]/g, '-').trim().toLowerCase();

    const canonMake = this.#data.options.find((e) => norm(e.make) === norm(rawMake));
    if (!canonMake) return;
    this._makeSelect.value = canonMake.make;
    this.#populateModels(canonMake.make);

    const rawModel = params.get('model');
    if (!rawModel) return;

    const canonModel = Array.from(this._modelSelect.options)
      .find((opt) => opt.value && norm(opt.value) === norm(rawModel));
    if (!canonModel) return;
    this._modelSelect.value = canonModel.value;
    this.#populateYears(canonMake.make, canonModel.value);

    const rawYear = params.get('year');
    if (!rawYear) return;

    const canonYear = Array.from(this._yearSelect.options)
      .find((opt) => opt.value && norm(opt.value) === norm(rawYear));
    if (!canonYear) return;
    this._yearSelect.value = canonYear.value;
    this.#populateBeds(canonMake.make, canonModel.value, canonYear.value);

    const rawBed = params.get('bed');
    if (!rawBed) return;

    const canonBed = Array.from(this._bedSelect.options)
      .find((opt) => opt.value && norm(opt.value) === norm(rawBed));
    if (!canonBed) return;
    this._bedSelect.value = canonBed.value;
    if (this._bedMirror) this._bedMirror.textContent = canonBed.value;

    const bedEntry = this.#findBedEntry(canonMake.make, canonModel.value, canonYear.value, canonBed.value);
    if (!bedEntry) return;

    const hasInbedOptions = bedEntry.inbeds.some((e) => e.inbed === 'Yes' || e.inbed === 'No');

    if (!hasInbedOptions) {
      this.#selectedInbedEntry = bedEntry.inbeds[0];
      await this.#handleSelectionComplete();
      return;
    }

    const rawInbed = params.get('inbed');
    if (!rawInbed) {
      // Inbed required but not in URL — show select, user picks manually
      this.#resetSelect(this._inbedSelect, this._inbedPlaceholder);
      this._inbedSelect.add(new Option('No', 'No'));
      this._inbedSelect.add(new Option('Yes', 'Yes'));
      this.#showField('inbedField', 'inbedSelect');
      return;
    }

    const canonInbed = ['No', 'Yes'].find((v) => norm(v) === norm(rawInbed));
    if (!canonInbed) return;

    this.#resetSelect(this._inbedSelect, this._inbedPlaceholder);
    this._inbedSelect.add(new Option('No', 'No'));
    this._inbedSelect.add(new Option('Yes', 'Yes'));
    this.#showField('inbedField', 'inbedSelect');
    this._inbedSelect.value = canonInbed;
    if (this._inbedMirror) this._inbedMirror.textContent = canonInbed;
    this.#selectedInbedEntry = bedEntry.inbeds.find(
      (e) => e.inbed === canonInbed || (canonInbed === 'No' && e.inbed === '')
    );
    if (!this.#selectedInbedEntry) return;

    await this.#handleSelectionComplete();
  }

  // -- Private: restore from localStorage --

  async #restoreFromLocalStorage() {
    let saved;
    try {
      const raw = localStorage.getItem('configuratorTruckInfo');
      if (!raw) return;
      saved = JSON.parse(raw);
      if (!saved?.make || saved.make === 'NA') return;
    } catch { return; }

    this._makeSelect.value = saved.make;
    if (!this._makeSelect.value) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: this.getSource(), mismatched_field: 'make', saved }, 'warning');
      return;
    }
    this.#populateModels(saved.make);

    this._modelSelect.value = saved.model;
    if (!this._modelSelect.value) {
      const norm = saved.model.replace(/–/g, '-');
      for (const opt of this._modelSelect.options) {
        if (opt.value && opt.value.replace(/–/g, '-') === norm) {
          this._modelSelect.value = opt.value;
          saved.model = opt.value;
          break;
        }
      }
    }
    if (!this._modelSelect.value) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: this.getSource(), mismatched_field: 'model', saved }, 'warning');
      return;
    }
    this.#populateYears(saved.make, saved.model);

    this._yearSelect.value = saved.year;
    if (!this._yearSelect.value) {
      const norm = saved.year.replace(/–/g, '-');
      for (const opt of this._yearSelect.options) {
        if (opt.value && opt.value.replace(/–/g, '-') === norm) {
          this._yearSelect.value = opt.value;
          saved.year = opt.value;
          break;
        }
      }
    }
    if (!this._yearSelect.value) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: this.getSource(), mismatched_field: 'year', saved }, 'warning');
      return;
    }
    this.#populateBeds(saved.make, saved.model, saved.year);

    this._bedSelect.value = saved.bed;
    if (!this._bedSelect.value) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: this.getSource(), mismatched_field: 'bed', saved }, 'warning');
      return;
    }
    if (this._bedMirror) this._bedMirror.textContent = saved.bed;

    const bedEntry = this.#findBedEntry(saved.make, saved.model, saved.year, saved.bed);
    if (!bedEntry) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: this.getSource(), mismatched_field: 'bed_entry', saved }, 'warning');
      return;
    }

    const hasInbedOptions = bedEntry.inbeds.some((e) => e.inbed === 'Yes' || e.inbed === 'No');
    if (hasInbedOptions && saved.inbed) {
      this.#resetSelect(this._inbedSelect, this._inbedPlaceholder);
      this._inbedSelect.add(new Option('No', 'No'));
      this._inbedSelect.add(new Option('Yes', 'Yes'));
      this.#showField('inbedField', 'inbedSelect');
      this._inbedSelect.value = saved.inbed;
      if (this._inbedMirror) this._inbedMirror.textContent = saved.inbed;
      this.#selectedInbedEntry = bedEntry.inbeds.find(
        (e) => e.inbed === saved.inbed || (saved.inbed === 'No' && e.inbed === '')
      );
    } else {
      this.#selectedInbedEntry = bedEntry.inbeds[0];
    }

    // Always fetch live — avoids stale accessory URLs from pre-launch sessions.
    // (configuratorUrlMap in localStorage is for the link rewriter, not restore.)
    const _normYear   = (s) => (s || '').replace(/–/g, '-').replace(/\s/g, '').toLowerCase();
    const _normInbed  = (v) => {
      if (!v || v === 'NA' || v === 'reset') return '';
      const l = v.toLowerCase();
      if (l === 'yes') return 'Yes';
      if (l === 'no')  return 'No';
      return '';
    };
    const urlKey = `${saved.make}|${saved.model}|${saved.year}|${saved.bed}|${_normInbed(saved.inbed)}`;
    const urlMap = await _getConfiguratorUrlMap();
    if (_isConfiguratorUrlMapFailed()) {
      this.#showFetchError();
      this.#reportError('configurator_url_map_fetch_failed', { source: this.getSource() });
    } else {
      this.#hideFetchError();
    }
    let urls = urlMap[urlKey] || {};
    if (!Object.keys(urls).length) {
      // en-dash fallback (year should already be canonical at this point, but guard anyway)
      const altKey = `${saved.make}|${saved.model}|${_normYear(saved.year)}|${saved.bed}|${_normInbed(saved.inbed)}`;
      urls = urlMap[altKey] || {};
    }

    // Heal a stale snapshot: the localStorage urlMap may have been saved before
    // accessory URLs existed. Refresh it from the live map so the link rewriter
    // stops marking now-available products as unavailable. Uses a dedicated event
    // (not configurator:update) to avoid re-entering restore.
    if (Object.keys(urls).length) {
      const freshJson = JSON.stringify(urls);
      if (freshJson !== localStorage.getItem('configuratorUrlMap')) {
        localStorage.setItem('configuratorUrlMap', freshJson);
        document.dispatchEvent(new CustomEvent('configurator:urlmap-refresh'));
      }
    }

    await this.onRestored(saved, urls);
  }

  // -- Private: data lookup --

  #findBedEntry(make, model, year, bed) {
    const makeEntry = this.#data.options.find((e) => e.make === make);
    const modelEntry = makeEntry?.models.find((e) => e.model === model);
    const yearEntry = modelEntry?.years.find((e) => e.year === year);
    return yearEntry?.beds.find((e) => e.bed === bed) || null;
  }

  // -- Private: UI helpers --

  #resetSelect(select, placeholder) {
    if (!select) return;
    select.innerHTML = '';
    select.add(new Option(placeholder, ''));
  }

  #showField(fieldRef, selectRef) {
    if (fieldRef === 'inbedField') this._inbedField?.removeAttribute('hidden');
    const select = this[`_${selectRef}`];
    select?.removeAttribute('disabled');
    requestAnimationFrame(() => select?.focus());
  }

  #hideFieldsFrom(startField) {
    const fields = ['modelField', 'yearField', 'bedField', 'inbedField'];
    const startIndex = fields.indexOf(startField);
    if (startIndex === -1) return;
    for (let i = startIndex; i < fields.length; i++) {
      const fieldName = fields[i];
      const selectName = fieldName.replace('Field', 'Select');
      const select = this[`_${selectName}`];
      if (fieldName === 'inbedField') this._inbedField?.setAttribute('hidden', '');
      if (select) {
        select.setAttribute('disabled', '');
        this.#resetSelect(select, select.options[0]?.text || '');
      }
      if (fieldName === 'bedField' && this._bedMirror) this._bedMirror.textContent = this._bedPlaceholder;
      if (fieldName === 'inbedField' && this._inbedMirror) this._inbedMirror.textContent = this._inbedPlaceholder;
    }
    this.#selectedInbedEntry = null;
  }

  #showFetchError() {
    this._fetchError?.removeAttribute('hidden');
  }

  #hideFetchError() {
    this._fetchError?.setAttribute('hidden', '');
  }

  // -- Private: external sync --

  #handleExternalUpdate = () => {
    const raw = localStorage.getItem('configuratorTruckInfo');
    if (!raw) {
      this._repopulateMakes();
      this.onReset();
    } else {
      this.#restoreFromLocalStorage();
    }
  };

  #handleStorageChange = (event) => {
    if (event.key !== 'configuratorTruckInfo') return;
    if (!event.newValue) {
      this._repopulateMakes();
      this.onReset();
    } else {
      this.#restoreFromLocalStorage();
    }
  };

  // -- Private: analytics --

  #reportError(eventName, params, level = 'error') {
    this.#pushGA4Event(eventName, params);
    if (window.Sentry) Sentry.captureMessage(eventName, { level, extra: params });
  }

  #pushGA4Event(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, params));
  }
}

window.ConfiguratorBase = ConfiguratorBase;

} // end if (!window.ConfiguratorBase)
